import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useOrganisations } from "../hooks/useOrganisations";
import { useDeleteOrganisation } from "../hooks/useDeleteOrganisation";
import { useDebounce } from "../hooks/useDebounce";
import { OrgWizard } from "../components/admin/OrgWizard";
import { Pagination } from "../components/ui/Pagination";
import { Spinner } from "../components/ui/Spinner";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { OrgSummary, ProvisionOrgResponse } from "../domain/types/admin.types";

const PAGE_SIZE = 20;

export const OrganisationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const debouncedSearch = useDebounce(search, 400);
  const { data, isLoading, isError, error } = useOrganisations(page, PAGE_SIZE, debouncedSearch);
  const deleteOrg = useDeleteOrganisation();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete organisation "${name}"? This cannot be undone.`)) return;
    setDeletingId(id);
    try {
      await deleteOrg.mutateAsync(id);
    } finally {
      setDeletingId(null);
    }
  };

  const handleCreated = (res: ProvisionOrgResponse) => {
    navigate(`/organisations/${res.organisationId}`);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Organisations</h2>
          {data && (
            <p className="text-xs text-gray-400 mt-0.5">{data.total} total</p>
          )}
        </div>
        <Button onClick={() => setWizardOpen(true)}>+ Create Organisation</Button>
      </div>

      {/* Search */}
      <Input
        value={search}
        onChange={handleSearchChange}
        placeholder="Search organisations…"
        className="w-full max-w-xs"
      />

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-400 py-8">
          <Spinner /> Loading organisations…
        </div>
      ) : isError ? (
        <div className="py-8 text-sm text-red-500">
          {(error as any)?.data?.message ?? "Failed to load organisations."}
        </div>
      ) : !data || data.organisations.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-400">
          {debouncedSearch ? `No organisations matching "${debouncedSearch}"` : "No organisations yet."}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded border border-border-gray bg-white">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-border-gray bg-default-99">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Created
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-gray">
                {data.organisations.map((org: OrgSummary) => (
                  <tr
                    key={org.id}
                    className="hover:bg-default-99 transition-colors cursor-pointer"
                    onClick={() => navigate(`/organisations/${org.id}`)}
                  >
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {org.name}
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {new Date(org.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => navigate(`/organisations/${org.id}`)}
                          className="text-xs text-link-blue hover:underline px-2 py-1"
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleDelete(org.id, org.name)}
                          disabled={deletingId === org.id}
                          className="text-xs text-red-500 hover:text-red-700 hover:underline px-2 py-1 disabled:opacity-40"
                        >
                          {deletingId === org.id ? "Deleting…" : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            size={PAGE_SIZE}
            total={data.total}
            onChange={setPage}
          />
        </>
      )}

      <OrgWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreated={handleCreated}
      />
    </div>
  );
};
